import { useEffect, useState } from "react";
import { RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Soft delete: a deleted record lands here, kept in full, and can be put
// back — or, when someone really wants it gone, deleted permanently (after a
// confirmation). `base` is "/contractor" or "/vendor".
export default function RecentlyDeleted({ base }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [purging, setPurging] = useState(null);

  const load = () => api.get(`${base}/recently-deleted`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`${base}/recently-deleted/${row.id}/restore`);
      toast.success(`${row.kind} ${row.label} restored`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't restore it");
    } finally {
      setBusy(null);
    }
  };

  const purge = async () => {
    const row = purging;
    setBusy(row.id);
    try {
      await api.delete(`${base}/recently-deleted/${row.id}`);
      toast.success(`${row.kind} ${row.label} permanently deleted`);
      setPurging(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete it");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="recently-deleted-page">
      <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><Trash2 size={22} className="text-slate-400" /> Recently Deleted</h1>
      <p className="text-slate-500 text-sm mt-1 max-w-2xl">
        Everything removed is kept here, exactly as it was, with who deleted it and when. Restore it to put it back, or delete it permanently to erase it for good.
      </p>
      <div className="overflow-x-auto mt-6">
        <table className="w-full text-[13px]" data-testid="recently-deleted-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2">What</th><th className="text-left py-2">Name / number</th>
              <th className="text-left py-2">Deleted</th><th className="text-left py-2">By</th><th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`deleted-row-${r.record_id}`}>
                <td className="py-2.5 text-slate-500">{r.kind}</td>
                <td className="py-2.5 font-medium">{r.label}</td>
                <td className="py-2.5 text-slate-500 whitespace-nowrap">{fmtDateTime(r.deleted_at)}</td>
                <td className="py-2.5 text-slate-500">{r.deleted_by_name || "—"}</td>
                <td className="py-2.5 text-right whitespace-nowrap">
                  <button disabled={busy === r.id} onClick={() => restore(r)} data-testid={`restore-${r.record_id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50 disabled:opacity-60">
                    <RotateCcw size={12} /> {busy === r.id ? "Restoring…" : "Restore"}
                  </button>
                  <button disabled={busy === r.id} onClick={() => setPurging(r)} data-testid={`purge-${r.record_id}`}
                    className="inline-flex items-center gap-1 ml-2 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-[12px] font-semibold hover:bg-red-50 disabled:opacity-60">
                    <Trash2 size={12} /> Delete permanently
                  </button>
                </td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400 text-sm">Nothing deleted.</td></tr>}
            {!rows && <tr><td colSpan={5} className="py-10 text-center text-slate-400 text-sm">Loading…</td></tr>}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!purging} onOpenChange={(v) => { if (!v && !busy) setPurging(null); }}>
        <AlertDialogContent data-testid="purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {purging?.label} permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This erases the {purging?.kind.toLowerCase()} and any files deleted with it. It can't be restored afterward. The audit trail keeps a record that it was permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={!!busy} onClick={(e) => { e.preventDefault(); purge(); }} data-testid="confirm-purge-button" className="bg-red-600 hover:bg-red-700">
              {busy ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
